import { type Model } from "./models";
import {
  OpenAIChatLanguageModel,
  type OpenAIChatSettings,
  OpenAICompletionLanguageModel,
  type OpenAICompletionSettings,
  OpenAIEmbeddingModel,
} from "@ai-sdk/openai/internal";
import type {
  EmbeddingModelV1,
  LanguageModelV1,
  ProviderV1,
} from "@ai-sdk/provider";
import type { FetchFunction } from "@ai-sdk/provider-utils";
import type { MODELS } from "./models";
import type { OpenAIEmbeddingSettings } from "@ai-sdk/openai/internal";

type CrosshatchSettings = {
  replace?: object;
};
type OpenAIModel = (typeof MODELS)[number];
type CrosshatchOpenAIChatSettings = OpenAIChatSettings & CrosshatchSettings;
type CrosshatchOpenAICompletionSettings = OpenAICompletionSettings &
  CrosshatchSettings;
type CrosshatchOpenAIEmbeddingSettings = OpenAIEmbeddingSettings &
  CrosshatchSettings;
// type CrosshatchGoogleVertexSettings = GoogleVertexSettings & CrosshatchSettings;

export interface CrosshatchProvider extends ProviderV1 {
  (
    deploymentId: OpenAIModel,
    settings?: CrosshatchOpenAIChatSettings,
  ): LanguageModelV1;

  /**
Creates a Crosshatch chat model for text generation.
   */
  languageModel(
    deploymentId: OpenAIModel,
    settings?: CrosshatchOpenAIChatSettings,
  ): LanguageModelV1;

  /**
Creates a Crosshatch model for text embeddings.
   */
  textEmbeddingModel(
    deploymentId: OpenAIModel,
    settings?: CrosshatchOpenAIEmbeddingSettings,
  ): EmbeddingModelV1<string>;
  // textEmbeddingModel(
  //   deploymentId: VertexModel,
  // ): EmbeddingModelV1<string>;
}

export interface CrosshatchProviderSettings {
  /**
   * Base URL for the Crosshatch API.
   */
  baseUrl?: string;
  /**
   * API key for authenticating requests.
   */
  token?: string | (() => string);

  /**
   * Custom headers to include in the requests.
   */
  headers?: Record<string, string>;

  /**
   * Custom fetch implementation. You can use it as a middleware to intercept requests,
   * or to provide a custom fetch implementation for e.g. testing.
   */
  fetch?: FetchFunction;
}

/**
Create a Crosshatch provider instance.
 */
export function createCrosshatchOpenAI({
  token,
  baseUrl = "https://ai.crosshatch.io",
  fetch: fetchFn = fetch,
  ...options
}: CrosshatchProviderSettings = {}) {
  const createFetch =
    (provider: string, replace: object) =>
    (input: string | URL | Request, init?: RequestInit) => {
      const { headers, url } =
        input instanceof Request
          ? {
              headers: new Headers(input.headers),
              url: new URL(input.url),
            }
          : {
              headers: new Headers(init?.headers),
              url: new URL(input),
            };
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("Crosshatch-Replace", JSON.stringify(replace));
      headers.set("Crosshatch-Provider", provider);
      url.searchParams.set("api-version", "2024-08-01-preview");
      if (input instanceof Request) {
        return fetchFn(new Request(url, { ...input, headers }));
      } else {
        return fetchFn(url, { ...init, headers });
      }
    };

  const url = ({ path }: { path: string }) => `${baseUrl}${path}`;

  function createChatModel(
    provider: Model,
    { replace, ...settings }: CrosshatchOpenAIChatSettings = {},
  ) {
    return new OpenAIChatLanguageModel(provider, settings, {
      provider: "azure-openai.chat",
      url,
      compatibility: "compatible",
      headers: () => options.headers ?? {},
      fetch: createFetch(provider, replace ?? {}),
    });
  }

  const createCompletionModel = (
    provider: Model,
    { replace, ...settings }: CrosshatchOpenAICompletionSettings = {},
  ) => {
    new OpenAICompletionLanguageModel(provider, settings, {
      provider: "azure-openai.completion",
      url,
      compatibility: "compatible",
      headers: () => options.headers ?? {},
      fetch: createFetch(provider, replace ?? {}),
    });
  };

  function createEmbeddingModel(
    provider: Model,
    { replace, ...settings }: CrosshatchOpenAIEmbeddingSettings = {},
  ) {
    return new OpenAIEmbeddingModel(provider, settings, {
      provider: "azure-openai.embedding",
      url,
      headers: () => options.headers ?? {},
      fetch: createFetch(provider, replace ?? {}),
    });
  }
  const provider = function (
    deploymentId: Model,
    settings?: CrosshatchOpenAIChatSettings,
  ) {
    if (new.target) {
      throw new Error(
        "The Crosshatch OpenAI model function cannot be called with the new keyword.",
      );
    }

    return createChatModel(
      deploymentId,
      settings as CrosshatchOpenAIChatSettings,
    );
  };

  provider.languageModel = createChatModel;
  provider.chat = createChatModel;
  provider.completion = createCompletionModel;
  provider.embedding = createEmbeddingModel;
  provider.textEmbedding = createEmbeddingModel;
  provider.textEmbeddingModel = createEmbeddingModel;
  return provider as CrosshatchProvider;
}
